<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { ref, nextTick } from 'vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { statusLabels } from './list';
const props = defineProps({
  modelValue: { type: Object, required: true },
  groups: { type: Array, required: true },
  loading: Boolean,
  error: { type: String, default: '' },
  cursor: { type: [Number, String], default: null },
  disabled: Boolean,
});
const emit = defineEmits(['update:modelValue', 'search', 'more']);
const statuses = [
  { value: '', label: 'Все статусы' },
  ...Object.entries(statusLabels).map(([value, label]) => ({ value, label })),
];
// Keep native visuals while making the installed combobox keyboard-selectable.
const groupField = ref(null);
const statusField = ref(null);
const selectKeys = ref({ group_id: 0, status: 0 });
async function selectByKey(event, key, options) {
  if (props.disabled) return;
  if (['ArrowDown', 'ArrowUp'].includes(event.key)) {
    event.preventDefault();
    const current = options.findIndex(
      option => option.value === props.modelValue[key]
    );
    const index = Math.max(
      0,
      Math.min(
        options.length - 1,
        current + (event.key === 'ArrowDown' ? 1 : -1)
      )
    );
    if (options[index])
      emit('update:modelValue', {
        ...props.modelValue,
        [key]: options[index].value,
      });
  } else if (
    event.key === 'Escape' ||
    (event.key === 'Enter' && event.target.type === 'search')
  ) {
    event.preventDefault();
    event.stopPropagation();
    selectKeys.value[key] += 1;
    await nextTick();
    (key === 'group_id' ? groupField.value : statusField.value)
      ?.querySelector('button')
      ?.focus();
  }
}
</script>

<template>
  <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 min-w-0">
    <div
      ref="groupField"
      class="min-w-0 flex flex-col gap-2"
      @keydown="selectByKey($event, 'group_id', groups)"
    >
      <span class="text-sm font-medium text-n-slate-12">Группа</span>
      <ComboBox
        :key="selectKeys.group_id"
        :model-value="modelValue.group_id"
        :options="groups"
        :disabled="disabled"
        use-api-results
        aria-label="Группа для фильтра"
        placeholder="Все группы"
        search-placeholder="Поиск группы"
        empty-state="Группы не найдены"
        @search="emit('search', $event)"
        @update:model-value="
          emit('update:modelValue', { ...modelValue, group_id: String($event) })
        "
      />
      <p v-if="loading" role="status" class="text-sm text-n-slate-11 m-0">
        Загрузка групп…
      </p>
      <p v-if="error" role="alert" class="text-sm text-n-ruby-9 m-0">
        {{ error }}
      </p>
      <div class="flex gap-2">
        <Button
          v-if="cursor"
          type="button"
          label="Следующие группы"
          slate
          ghost
          :disabled="loading || disabled"
          @click="emit('more')"
        />
        <Button
          v-if="error"
          type="button"
          label="Повторить загрузку групп"
          slate
          ghost
          @click="emit('search', '')"
        />
      </div>
    </div>
    <div
      ref="statusField"
      class="min-w-0 flex flex-col gap-2"
      @keydown="selectByKey($event, 'status', statuses)"
    >
      <span class="text-sm font-medium text-n-slate-12">Статус</span>
      <ComboBox
        :key="selectKeys.status"
        :model-value="modelValue.status"
        :options="statuses"
        :disabled="disabled"
        aria-label="Статус размещения"
        placeholder="Все статусы"
        search-placeholder="Поиск статуса"
        @update:model-value="
          emit('update:modelValue', { ...modelValue, status: $event })
        "
      />
    </div>
    <Input
      :model-value="modelValue.date_from"
      type="date"
      label="Действует с"
      :disabled="disabled"
      @update:model-value="
        emit('update:modelValue', { ...modelValue, date_from: $event })
      "
    />
    <Input
      :model-value="modelValue.date_to"
      type="date"
      label="Действует до"
      :disabled="disabled"
      @update:model-value="
        emit('update:modelValue', { ...modelValue, date_to: $event })
      "
    />
    <p class="text-xs text-n-slate-11 m-0 lg:col-span-2">
      Покажем размещения, действующие хотя бы один день в выбранном периоде.
      Даты включительно, по Москве.
    </p>
    <Input
      :model-value="modelValue.payment_from"
      type="date"
      label="Оплачено с"
      :disabled="disabled"
      @update:model-value="
        emit('update:modelValue', { ...modelValue, payment_from: $event })
      "
    />
    <Input
      :model-value="modelValue.payment_to"
      type="date"
      label="Оплачено по"
      :disabled="disabled"
      @update:model-value="
        emit('update:modelValue', { ...modelValue, payment_to: $event })
      "
    />
    <p class="text-xs text-n-slate-11 m-0 lg:col-span-2">
      Даты оплаты включительно. Бесплатные размещения и оплаты без даты в этот
      диапазон не входят.
    </p>
  </div>
</template>
